import { useState, useRef, useEffect, useMemo } from 'react';
import { Play, Pause, SkipBack, SkipForward, Square, Repeat, X, FileText, Volume2, VolumeX, Volume1, ListMusic } from 'lucide-react';
import { usePlayer, formatTime } from '@/context/PlayerContext';
import { cn } from '@/lib/utils';
import { QueuePanel } from './QueuePanel';

const RATES = [0.5, 0.75, 1, 1.25, 1.5];

export function PlayerBar() {
  const {
    currentTrack,
    playbackState,
    currentTime,
    duration,
    playbackRate,
    volume,
    loop,
    playlist,
    playlistIndex,
    isPlayerVisible,
    contentView,
    pause,
    resume,
    stop,
    seek,
    seekRelative,
    setPlaybackRate,
    setVolume,
    setLoopPointA,
    setLoopPointB,
    clearLoop,
    closePlayer,
    toggleLyricsView,
  } = usePlayer();

  // Volume slider state
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [previousVolume, setPreviousVolume] = useState(1);
  const volumeRef = useRef<HTMLDivElement>(null);

  // Queue panel state
  const [showQueuePanel, setShowQueuePanel] = useState(false);

  // Close volume slider when clicking outside
  useEffect(() => {
    if (!showVolumeSlider) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (volumeRef.current && !volumeRef.current.contains(e.target as Node)) {
        setShowVolumeSlider(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showVolumeSlider]);

  const handleVolumeClick = () => {
    if (volume > 0) {
      setPreviousVolume(volume);
      setVolume(0);
    } else {
      setVolume(previousVolume || 1);
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVolume(parseFloat(e.target.value));
  };

  const getVolumeIcon = () => {
    if (volume === 0) return VolumeX;
    if (volume < 0.5) return Volume1;
    return Volume2;
  };

  const VolumeIcon = getVolumeIcon();

  // Convert playlist to queue format for QueuePanel
  const queueTracks = useMemo(() => {
    if (!playlist) return [];
    return playlist.slice(playlistIndex + 1).map(item => ({
      id: item.track.id,
      title: item.track.song.title,
      artist: item.track.song.artist,
      coverImage: item.track.song.coverImage,
      voicePart: item.track.name,
    }));
  }, [playlist, playlistIndex]);

  const currentQueueTrack = currentTrack ? {
    id: currentTrack.id,
    title: currentTrack.title,
    artist: currentTrack.artist || null,
    coverImage: currentTrack.coverUrl || null,
    voicePart: currentTrack.trackName,
  } : null;

  if (!isPlayerVisible || !currentTrack) return null;

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;
  const isPlaying = playbackState === 'playing';
  const isLoading = playbackState === 'loading';

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = x / rect.width;
    seek(percent * duration);
  };

  return (
    <div className="hidden lg:block fixed bottom-0 left-0 right-0 bg-bg-elevated border-t border-subtle z-50">
      {/* Progress bar (clickable) */}
      <div
        className="h-1 bg-bg-input cursor-pointer group"
        onClick={handleProgressClick}
      >
        {/* Loop region */}
        {loop.pointA !== null && loop.pointB !== null && (
          <div
            className="absolute h-1 bg-zzing-purple-500/30"
            style={{
              left: `${(loop.pointA / duration) * 100}%`,
              width: `${((loop.pointB - loop.pointA) / duration) * 100}%`,
            }}
          />
        )}
        <div
          className="h-full bg-zzing-purple-500 transition-all relative"
          style={{ width: `${progressPercent}%` }}
        >
          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover:opacity-100 shadow-md" />
        </div>
      </div>

      <div className="flex items-center gap-4 px-4 py-2">
        {/* Track info */}
        <div className="flex items-center gap-3 min-w-[200px] max-w-[300px]">
          {currentTrack.coverUrl ? (
            <img
              src={currentTrack.coverUrl}
              alt={currentTrack.title}
              className="w-12 h-12 rounded object-cover"
            />
          ) : (
            <div className="w-12 h-12 rounded bg-zzing-purple-500/20 flex items-center justify-center">
              <span className="text-zzing-purple-500 text-lg">♪</span>
            </div>
          )}
          <div className="min-w-0">
            <p className="text-sm font-medium text-text-primary truncate">
              {currentTrack.title}
            </p>
            <p className="text-xs text-text-secondary truncate">
              {currentTrack.trackName || currentTrack.artist}
            </p>
          </div>
        </div>

        {/* Main controls */}
        <div className="flex-1 flex flex-col items-center max-w-[600px] mx-auto">
          <div className="flex items-center gap-2">
            <button
              onClick={() => seekRelative(-10)}
              className="p-2 rounded-full text-text-secondary hover:text-text-primary hover:bg-bg-elevated-hover transition-colors"
              title="10s terug"
            >
              <SkipBack className="w-4 h-4" />
            </button>

            <button
              onClick={stop}
              className="p-2 rounded-full text-text-secondary hover:text-text-primary hover:bg-bg-elevated-hover transition-colors"
              title="Stop"
            >
              <Square className="w-4 h-4" />
            </button>

            <button
              onClick={() => isPlaying ? pause() : resume()}
              className="p-3 rounded-full bg-white text-bg-base hover:scale-105 transition-transform"
              title={isPlaying ? 'Pauze' : 'Afspelen'}
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-bg-base border-t-transparent rounded-full animate-spin" />
              ) : isPlaying ? (
                <Pause className="w-5 h-5" />
              ) : (
                <Play className="w-5 h-5 ml-0.5" />
              )}
            </button>

            <button
              onClick={() => seekRelative(10)}
              className="p-2 rounded-full text-text-secondary hover:text-text-primary hover:bg-bg-elevated-hover transition-colors"
              title="10s vooruit"
            >
              <SkipForward className="w-4 h-4" />
            </button>
          </div>

          {/* Time display */}
          <div className="flex items-center gap-2 text-xs text-text-muted mt-1">
            <span className="font-mono w-10 text-right">{formatTime(currentTime)}</span>
            <span>/</span>
            <span className="font-mono w-10">{formatTime(duration)}</span>
          </div>
        </div>

        {/* Right controls */}
        <div className="flex items-center gap-3 min-w-[200px] justify-end">
          {/* Speed selector */}
          <div className="flex items-center gap-1">
            {RATES.map(rate => (
              <button
                key={rate}
                onClick={() => setPlaybackRate(rate)}
                className={cn(
                  'px-2 py-1 rounded text-xs font-medium transition-colors',
                  playbackRate === rate
                    ? 'bg-zzing-purple-500 text-white'
                    : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated-hover'
                )}
              >
                {rate}x
              </button>
            ))}
          </div>

          {/* A-B Loop */}
          <div className="flex items-center gap-1">
            <button
              onClick={setLoopPointA}
              className={cn(
                'px-2 py-1 rounded text-xs font-medium transition-colors',
                loop.pointA !== null
                  ? 'bg-green-500/20 text-green-400'
                  : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated-hover'
              )}
              title="Zet punt A"
            >
              A
            </button>
            <button
              onClick={setLoopPointB}
              disabled={loop.pointA === null}
              className={cn(
                'px-2 py-1 rounded text-xs font-medium transition-colors',
                loop.pointB !== null
                  ? 'bg-red-500/20 text-red-400'
                  : loop.pointA === null
                    ? 'text-text-muted'
                    : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated-hover'
              )}
              title="Zet punt B"
            >
              B
            </button>
            {loop.active && (
              <button
                onClick={clearLoop}
                className="p-1 rounded text-text-secondary hover:text-text-primary"
                title="Reset loop"
              >
                <Repeat className="w-4 h-4 text-zzing-purple-500" />
              </button>
            )}
          </div>

          {/* Volume control */}
          <div className="relative" ref={volumeRef}>
            <button
              onClick={handleVolumeClick}
              onMouseEnter={() => setShowVolumeSlider(true)}
              className="p-2 rounded-full text-text-secondary hover:text-text-primary hover:bg-bg-elevated-hover transition-colors"
              title={volume === 0 ? 'Unmute' : 'Mute'}
            >
              <VolumeIcon className="w-4 h-4" />
            </button>

            {/* Volume slider popup */}
            {showVolumeSlider && (
              <div
                className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 p-3 bg-bg-elevated rounded-lg shadow-lg border border-subtle"
                onMouseLeave={() => setShowVolumeSlider(false)}
              >
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={volume}
                  onChange={handleVolumeChange}
                  className="w-24 h-1 accent-zzing-purple-500 cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, var(--zzing-purple-500) 0%, var(--zzing-purple-500) ${volume * 100}%, var(--bg-input) ${volume * 100}%, var(--bg-input) 100%)`
                  }}
                />
                <div className="text-xs text-text-muted text-center mt-1">
                  {Math.round(volume * 100)}%
                </div>
              </div>
            )}
          </div>

          {/* Lyrics toggle */}
          <button
            onClick={toggleLyricsView}
            className={cn(
              'p-2 rounded-full transition-colors',
              contentView === 'lyrics'
                ? 'text-zzing-purple-500 bg-zzing-purple-500/20'
                : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated-hover'
            )}
            title={contentView === 'lyrics' ? 'Verberg tekst' : 'Toon tekst'}
          >
            <FileText className="w-4 h-4" />
          </button>

          {/* Queue toggle */}
          <button
            onClick={() => setShowQueuePanel(!showQueuePanel)}
            className={cn(
              'p-2 rounded-full transition-colors',
              showQueuePanel
                ? 'text-zzing-purple-500 bg-zzing-purple-500/20'
                : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated-hover'
            )}
            title={showQueuePanel ? 'Verberg wachtrij' : 'Toon wachtrij'}
          >
            <ListMusic className="w-4 h-4" />
          </button>

          {/* Close button */}
          <button
            onClick={closePlayer}
            className="p-2 rounded-full text-text-secondary hover:text-text-primary hover:bg-bg-elevated-hover transition-colors"
            title="Sluiten"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Queue Panel */}
      <QueuePanel
        isOpen={showQueuePanel}
        onClose={() => setShowQueuePanel(false)}
        currentTrack={currentQueueTrack}
        queue={queueTracks}
        onPlayFromQueue={() => {}}
        onRemoveFromQueue={() => {}}
      />
    </div>
  );
}
